import { Link } from 'react-router-dom'
import { ShoppingCart, Badge, Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCartStore, useAuthStore } from '@/lib/store'
import { Toast } from '@/components/ui/toast'
import { useState, useEffect } from 'react'
import type { Product } from '@/types/database'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'

interface ProductCardProps {
  product: Product & {
    seller?: {
      is_verified?: boolean
    }
  }
}

export function ProductCard({ product }: ProductCardProps) {
  const addToCart = useCartStore((state) => state.addItem)
  const user = useAuthStore((state) => state.user)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [isWishlisted, setIsWishlisted] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (user) {
      checkWishlistStatus()
    }
  }, [user, product.id])

  const checkWishlistStatus = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('wishlists')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', product.id)
        .maybeSingle()

      if (error) throw error
      setIsWishlisted(!!data)
    } catch (err) {
      console.error('Error checking wishlist status:', err)
    }
  }

  const toggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!user) {
      setToastMessage('Veuillez vous connecter pour ajouter aux favoris')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      if (isWishlisted) {
        // Remove from wishlist
        const { error } = await supabase
          .from('wishlists')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', product.id)

        if (error) throw error
        setIsWishlisted(false)
        setToastMessage('Retiré des favoris')
      } else {
        // Add to wishlist
        const { error } = await supabase
          .from('wishlists')
          .insert([{ user_id: user.id, product_id: product.id }])

        if (error) throw error
        setIsWishlisted(true)
        setToastMessage('Ajouté aux favoris')
      }
      setToastType('success')
      setShowToast(true)
    } catch (err) {
      console.error('Error updating wishlist:', err)
      setToastMessage('Une erreur est survenue')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Create a new product object with default selections
    // Check if colors and sizes exist and if so, select the first ones by default
    const productToAdd = {
      ...product,
      selectedColor: product.colors && product.colors.length > 0 ? product.colors[0] : undefined,
      selectedSize: product.sizes && product.sizes.length > 0 ? product.sizes[0] : undefined
    }
    
    addToCart(productToAdd)
    setToastMessage(t('products.addedToCart'))
    setToastType('success')
    setShowToast(true)
  }

  return (
    <div className="group relative h-full flex flex-col">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <Link to={`/products/${product.id}`} className="block flex-grow">
        <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100 border border-gray-200 relative">
          <img
            src={product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
            alt={product.title}
            className="h-full w-full object-cover object-center group-hover:opacity-90 transition-opacity"
          />
          <button 
            onClick={toggleWishlist}
            className="absolute top-2 right-2 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
          >
            <Heart className={`h-4 w-4 ${isWishlisted ? 'text-red-500 fill-current' : 'text-gray-600'}`} />
          </button>
        </div>
        <div className="mt-4 flex justify-between">
          <div className="max-w-[70%]">
            <div className="flex items-center">
              <h3 className="text-sm text-gray-900 font-medium line-clamp-1">
                <span aria-hidden="true" className="absolute inset-0" />
                {product.title}
              </h3>
              {product.seller?.is_verified && (
                <Badge className="h-3.5 w-3.5 ml-1 text-blue-500" />
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500 line-clamp-1">{product.category}</p>
          </div>
          <p className="text-sm font-medium text-amber-600">${product.price}</p>
        </div>
      </Link>
      <div className="mt-4">
        <Button
          variant="primary"
          size="sm"
          className="w-full text-xs sm:text-sm flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
          onClick={handleAddToCart}
        >
          <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span>{t('products.addToCart')}</span>
        </Button>
      </div>
    </div>
  )
}